/**
 * KycVerification — admin dashboard for eKYC.
 *
 * - Summary cards: total verified, pending, by-type breakdown
 * - Employee selector: pick an employee → expands EmployeeKycTab below
 * - Recent verifications log with status filter + verification-type filter
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, CheckCircle2, XCircle, AlertCircle, Search, Users } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import EmployeeKycTab from '../components/EmployeeKycTab';
import KycBulkUpload from '../components/KycBulkUpload';
import KycSettings from '../components/KycSettings';
import { listKycVerifications, getKycSummary, getEmployees } from '../api';

const STATUS_FILTERS = ['all', 'verified', 'failed', 'needs_review'];

export default function KycVerification({ user, setUser }) {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [loadingRecent, setLoadingRecent] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, r, emp] = await Promise.all([
          getKycSummary(),
          listKycVerifications({
            limit: 100,
            ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          }),
          getEmployees(),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setRecent(r.data.items || []);
        const empList = Array.isArray(emp.data) ? emp.data : (emp.data?.items || emp.data?.employees || []);
        setEmployees(empList);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to load KYC dashboard');
      }
    })();
    return () => { cancelled = true; };
  }, []);  // initial-load only; status filter has its own effect below

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingRecent(true);
      try {
        const r = await listKycVerifications({
          limit: 100,
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        });
        if (!cancelled) setRecent(r.data.items || []);
      } finally {
        if (!cancelled) setLoadingRecent(false);
      }
    })();
    return () => { cancelled = true; };
  }, [statusFilter]);

  const filteredEmployees = useMemo(() => {
    if (!search) return employees.slice(0, 20);
    const q = search.toLowerCase();
    return employees.filter((e) =>
      (e.full_name || e.name || '').toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q) ||
      (e.employee_id || e.id || '').toString().toLowerCase().includes(q)
    ).slice(0, 20);
  }, [employees, search]);

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 space-y-6" data-testid="kyc-verification-page">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <ShieldCheck size={26} className="text-cyan-600" />
              eKYC Verification
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Run PAN, Bank, IFSC and Document OCR checks for any employee.
            </p>
          </div>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="kyc-summary-cards">
            <SummaryCard
              label="Total Verifications"
              value={summary.total_verifications}
              icon={ShieldCheck}
              colorClass="from-cyan-500 to-blue-600"
              testId="kyc-summary-total"
            />
            <SummaryCard
              label="Successfully Verified"
              value={summary.total_verified}
              icon={CheckCircle2}
              colorClass="from-emerald-500 to-green-600"
              testId="kyc-summary-verified"
            />
            <SummaryCard
              label="Verification Types"
              value={Object.keys(summary.by_type || {}).length}
              icon={Users}
              colorClass="from-violet-500 to-purple-600"
              testId="kyc-summary-types"
            />
          </div>
        )}

        {/* By-type breakdown */}
        {summary && Object.keys(summary.by_type || {}).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Breakdown by Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(summary.by_type).map(([type, counts]) => (
                  <div key={type} className="rounded-lg border border-slate-200 bg-white px-3 py-2 min-w-[160px]" data-testid={`kyc-breakdown-${type}`}>
                    <div className="text-xs text-slate-500 capitalize">{type.replace(/_/g, ' ')}</div>
                    <div className="flex gap-2 mt-1 text-xs">
                      {Object.entries(counts).map(([s, count]) => (
                        <span key={s} className="flex items-center gap-1">
                          {s === 'verified' && <CheckCircle2 size={12} className="text-emerald-600" />}
                          {s === 'failed' && <XCircle size={12} className="text-red-600" />}
                          {s === 'needs_review' && <AlertCircle size={12} className="text-amber-600" />}
                          <span className="font-semibold">{count}</span>
                          <span className="text-slate-500">{s}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Employee picker + KYC tab */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users size={16} className="text-slate-500" />
              Run KYC for an Employee
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or employee ID…"
                className="pl-9"
                data-testid="kyc-employee-search"
              />
            </div>
            {!selectedEmp && (
              <div className="border rounded-md max-h-64 overflow-y-auto" data-testid="kyc-employee-list">
                {filteredEmployees.length === 0 ? (
                  <div className="p-4 text-sm text-slate-500 text-center">No employees match.</div>
                ) : (
                  filteredEmployees.map((emp) => {
                    const eid = emp.employee_id || emp.id;
                    return (
                      <button
                        key={eid}
                        type="button"
                        onClick={() => setSelectedEmp(emp)}
                        className="w-full flex items-center justify-between px-4 py-2 hover:bg-slate-50 border-b last:border-b-0 text-left"
                        data-testid={`kyc-employee-row-${eid}`}
                      >
                        <div>
                          <div className="text-sm font-medium">{emp.full_name || emp.name}</div>
                          <div className="text-xs text-slate-500">{emp.email} · {eid}</div>
                        </div>
                        <Badge variant="outline" className="text-xs">Select</Badge>
                      </button>
                    );
                  })
                )}
              </div>
            )}
            {selectedEmp && (
              <div className="space-y-3" data-testid="kyc-selected-employee-panel">
                <div className="flex items-center justify-between p-3 rounded-md bg-cyan-50 border border-cyan-200">
                  <div>
                    <div className="text-sm font-semibold">{selectedEmp.full_name || selectedEmp.name}</div>
                    <div className="text-xs text-slate-600">{selectedEmp.email} · {selectedEmp.employee_id || selectedEmp.id}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedEmp(null)}
                    data-testid="kyc-clear-selected-btn"
                  >
                    Change employee
                  </Button>
                </div>
                <EmployeeKycTab
                  employeeId={selectedEmp.employee_id || selectedEmp.id}
                  employeeName={selectedEmp.full_name || selectedEmp.name}
                  employeeEmail={selectedEmp.email}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bulk upload + onboarding settings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <KycBulkUpload />
          <KycSettings />
        </div>

        {/* Recent verifications */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="text-base">Recent Verifications</CardTitle>
              <div className="flex gap-1.5" data-testid="kyc-status-filter">
                {STATUS_FILTERS.map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={statusFilter === s ? 'default' : 'outline'}
                    onClick={() => setStatusFilter(s)}
                    className="capitalize h-7 px-2 text-xs"
                    data-testid={`kyc-filter-${s}`}
                  >
                    {s.replace('_', ' ')}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingRecent ? (
              <div className="text-sm text-slate-500">Loading…</div>
            ) : recent.length === 0 ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                No verifications match the current filter.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="kyc-recent-table">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">When</th>
                      <th className="text-left px-3 py-2">Employee</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Verified by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                          {new Date(r.verified_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-xs">{r.employee_id || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-2 text-xs capitalize">{r.verification_type.replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.verified_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}


function SummaryCard({ label, value, icon: Icon, colorClass, testId }) {
  return (
    <div
      className={`rounded-xl bg-gradient-to-br ${colorClass} text-white p-4 shadow-sm`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase opacity-80">{label}</div>
          <div className="text-3xl font-bold mt-1">{value}</div>
        </div>
        <Icon size={28} className="opacity-70" />
      </div>
    </div>
  );
}


function StatusPill({ status }) {
  const map = {
    verified: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    failed: 'bg-red-100 text-red-700 border-red-200',
    needs_review: 'bg-amber-100 text-amber-700 border-amber-200',
    pending: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return (
    <Badge variant="outline" className={map[status] || map.pending}>
      {status.replace('_', ' ')}
    </Badge>
  );
}
